/** Configurações da UI
 * @typedef {Object} UISettings
 * @property {string} dialog-class            - Nome da classe css da dialog
 * @property {string} refresh-button-text     - Texto do botão de atualização de certificados
 * @property {string} refresh-button-class    - Nome da classe css do botão de atualização de certificados
 * @property {string} sign-button-text        - Texto do botão de assinatura
 * @property {string} sign-button-class       - Nome da classe do botão de assinatura
 * @property {string} cancel-button-text      - Texto do botão de cancelar
 * @property {string} cancel-button-class     - Nome da classe do botão de cancelar
 * @property {string} sel-cert-list           - Seletor para dropdown da lista de certificados
 * @property {string} sel-progress            - Seletor da progressbar
 * @property {string} sel-progress-text       - Seletor para texto da progressbar
 * @property {string} sel-timer               - Seletor para contador de tempo
 * @property {number} dialog-close-delay=500  - Tempo em milisegundos para continuar mostrando a dialog depois de finalizar o processo
 */

/** Informações de certificado
 * @typedef {Object} Certificate
 * @property {string} certID            - Id do certificado
 * @property {string} subjectDN         - DistinguishedName do requerente
 * @porperty {string} issuerDN          - DistinguishedName do emissor do certificado
 * @property {string} serial            - Serial do certificado
 * @property {string} notBefore         - Válido a partir de
 * @property {string} notAfter          - Válido até
 * @property {boolean} isValid          - Indica se certificado está válido
 * @property {boolean} hasKey           - Indica se certificado possui chave privada
 * @property {string} keyStoreType      - Define tipo da KeyStore
 * @property {Certificate[]}  certChain - Lista de certificados do caminho de certificação
 */

/** Resultado de requisição para listar certificados
 * @typedef {Object} ListCertificateResult
 * @prop {Certificate[]} certs  - Lista de certificados
 */

/** Controle responsável por criar dialog com certificados para utilizar na assinatura
 * @param {ViewServices} service   - Objeto para comunicação com webservice de assinatura
 * @param {string} dialogSelector  - Seletor para localização da modal de assinatura
 * @param {UISettings} uiSettings  - Objeto com parametrizações da UI
 * @class
 */
function ViewController(service, dialogSelector, uiSettings) {
    this.service = service;
    this.certificateList = null;
    this.myDefaultLocalStorage = "QCERT_Default_Certificate";

    this.dialogSelector = dialogSelector;
    this.dialog = null;
    this.event = new EE();

    this._currentProcess = 0;

    //settings para user interface, aplicando os overrides passados
    this.uiSettings = {
        "dialog-class"         : "qs-cert-dialog",
        "refresh-button-text"  : "Atualizar certificados",
        "refresh-button-class" : "qs-cert-refresh-button",
        "sign-button-text"     : "Assinar",
        "sign-button-class"    : "qs-cert-sign-button",
        "cancel-button-text"   : "Cancelar",
        "cancel-button-class"  : "qs-cert-cancel-button",
        "sel-cert-list"        : ".qs-cert-list",
        "sel-progress"         : ".qs-cert-progress",
        "sel-progress-text"    : ".qs-cert-progress-text",
        "sel-timer"            : ".qs-cert-timer",
        "dialog-close-delay"   : 500
    };
    jQuery.extend(this.uiSettings, uiSettings);
}

ViewController.PROC_NONE   = 0;
ViewController.PROC_SIGN   = 1;
ViewController.PROC_CANCEL = 2;

/** Inicializa objeto, setando configurações da UI e buscando lista de certificados
 * @instance
 */
ViewController.prototype.initialize = function () {
    this.dialog = $(this.dialogSelector).dialog({
        resizable: false,
        autoOpen: false,
        width: 430,
        dialogClass: this.uiSettings["dialog-class"],
        buttons: [{
            text : this.uiSettings["refresh-button-text"],
            class: this.uiSettings["refresh-button-class"],
            click: () => {
                this.refreshCertificates(true);
            }
        }, {
            text : this.uiSettings["sign-button-text"],
            class: this.uiSettings["sign-button-class"],
            click: () => {
                this._onSignButtonClick();
            }
        }, {
            text : this.uiSettings["cancel-button-text"],
            class: this.uiSettings["cancel-button-class"],
            click: () => {
                this._onCancelButtonClick();
            }
        }],
            open: (event, ui) => {
                if (this.dialog) {
                    this.dialog.find(this.uiSettings["sel-cert-list"]).attr("disabled", false);

                    //habilita os botões de ação (se precisar)
                    this._enterInProcess(ViewController.PROC_NONE);
                    
                    let progressEl = this.dialog.find(this.uiSettings["sel-progress"]);
                    if (progressEl.hasClass("ui-progressbar")) {
                        progressEl.progressbar("destroy");
                    }
                    this.dialog.find(this.uiSettings["sel-progress-text"]).text("");
                }
            },
            close: (event, ui) => {
                if (this.service.hasActiveSession()) {
                    this.service.cancelCurrentProcess();
                }
            }
    });
    this.refreshCertificates(false);
};

/** Retorna dialog
 * @instance
 */
ViewController.prototype.getDialog = function() {
    return this.dialog;
};

/** Compara DN de 2 certificados para efeitos de ordenação
 * @param {Certificate} a  - Certificado para comparação
 * @param {Certificate} b  - Certificado para comparação
 * @returns 0 se forem iguais, -1 se "a" for menor que "b", senão 1
 * @instance 
 */
ViewController.prototype.compareCertificates = function (a, b) {
    if (a.subjectDN < b.subjectDN)
        return -1;
    if (a.subjectDN > b.subjectDN)
        return 1;
    return 0;
};

/** Atualiza lista de certificados
 * @param {boolean} reloadCertificateCache - Define se deve limpar cache de certificados
 * @fires EE#updateRequired
 * @fires EE#onDisplayError
 * @instance
 */
ViewController.prototype.refreshCertificates = function (reloadCertificateCache)
{
    var certListSelector = this.uiSettings["sel-cert-list"];
    var jCertList = this.dialog.find(certListSelector);
    
    jCertList.find("option").remove();
    jCertList.append('<option>Carregando certificados...</option>').val("");
    
    this.service.listCertificates(reloadCertificateCache).then(
        response => {
            if (response.error) throw response.error;

            var result = response.result;
            this.certificateList = result.certs;
            var certificates = [];
            for (var i = 0; i < result.certs.length; i++) {
                if (result.certs[i].isValid)
                    certificates.push({
                        certID: result.certs[i].certID,
                        subjectDN: result.certs[i].subjectDN,
                        notAfter: result.certs[i].notAfter
                    });
            }

            certificates.sort(this.compareCertificates);

            var dropdown = this.dialog.find(certListSelector);
            dropdown.find("option").remove();
            
            for (var j = 0; j < certificates.length; j++) {
                dropdown.append(
                    $("<option></option>")
                        .val(certificates[j].certID)
                        .html(this.extractSubjectName(certificates[j].subjectDN) + " válido até: " + this.formatDate(certificates[j].notAfter))
                );
            }
            this.event.emit("certificatesRefreshed", null);
        }
    )
    .catch((err) => {
        if (QuickSoft.Signer.VersionCheckError.prototype.isPrototypeOf(err)) {
            return this.event.emit("updateRequired", err);
        }
        
        this.event.emit("onDisplayError", err);
    });
};

/** Define handler para refresh de certificados
 * @param {function} handler  - Processo a ser executado 
 * @instance
 */
ViewController.prototype.onCertificatesRefreshed = function (handler) {
    this.event.on("certificatesRefreshed", handler);
}

/** Define handler para atualização
 * @param {function} handler  - Processo a ser executado 
 * @instance
 */
ViewController.prototype.onUpdateRequired = function (handler) {
    this.event.on("updateRequired", handler);
}

/** Define handler para final do processo de assinatura
 * @param {function} handler - Processo a ser executado
 * @instance
 */
ViewController.prototype.onSignatureFinished = function (handler) {
    this.event.on("signatureFinished", handler);
}

/** Extrai CN do certificado, com um limite de 35 caracteres
 * @param {string} subjectDN  - Distinct name do certificado
 * @returns {string} Common name do certificado
 * @instance
 */
ViewController.prototype.extractSubjectName = function (subjectDN) {
    var myRegexp = /\bCN=(.[^,]*)/g;
    var match = myRegexp.exec(subjectDN);
    if (match) {
        return match[1].substring(0, 35);
    } else {
        return subjectDN.substring(0, 35);
    }
};

/** Formata data do certificado em string
 * @param {string} input  - data a ser formatada
 * @returns {string} Data formatada
 * @instance
 */
ViewController.prototype.formatDate = function (input) {
    return new Date(input).toLocaleDateString('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).replace(/\./g, '/');
};

/** Define certificado selecionado por padrão quando abrir a dialog
 * @param {string} valorCertId  - Id do certificado padrão
 * @instance
 */
ViewController.prototype.setDefaultCertificate = function (valorCertId) {

    if (typeof (Storage) != "undefined") {
        if (valorCertId != null) {
            localStorage.setItem(this.myDefaultLocalStorage, valorCertId);
        }
    }
}

/** Define handler para exibição de erros
 * @param {function} handler  - Processo a ser executado
 * @instance
 */
ViewController.prototype.onDisplayError = function (handler) {
    this.event.on("onDisplayError", handler)
}

/** Busca certificado selecionado
 * @returns {HTMLElement} Elemento com informações do certificado selecionado, ou null se não tem certificado selecionado
 * @instance
 */
ViewController.prototype.getSelectedCertificate = function () {
    //verifica se tem lista de certificados carregada
    if (!this.certificateList || this.certificateList.length < 1) return null;

    //pega element <select> e pega certID selecionado
    var selectedCertId = this.dialog.find(this.uiSettings["sel-cert-list"]).val();
    var match = this.certificateList.filter(function (v) {
        return v.certID == selectedCertId;
    });

    //retorna elemento que faz match do selecionado
    return match && match.length > 0 ? match[0] : null;
};

/**
 * Habilita botões de ação do controle de assinatura
 */
ViewController.prototype._enableButtons = function()
{
    //pega janela pai
    var parentWindow = this.dialog.parent();

    var buttons = [
        "." + this.uiSettings["refresh-button-class"],
        "." + this.uiSettings["sign-button-class"],
        "." + this.uiSettings["cancel-button-class"]
    ];

    var selector = buttons.join(",");
    parentWindow.find(selector).removeAttr("disabled");
};

/**
 * Desabilita botões à partir de suas classes
 * @param {...string} classNames 
 */
ViewController.prototype._disableButtonsByClass = function(...classNames)
{
    //pega janela pai
    var parentWindow = this.dialog.parent();

    //adiciona '.' para transformar em seletor e depois faz join para aplicar a vírgula (multiplos seletores)
    classNames = classNames.map(function(v) { return "." + v; });
    var selector = classNames.join(",");

    //desabilita os botões passados
    parentWindow.find(selector).attr("disabled", "disabled");
};

ViewController.prototype._onCancelButtonClick = function() {
    //garante que não estamos no meio de outro processo de cancelamento na UI
    if (this._currentProcess === ViewController.PROC_CANCEL) return;

    //desabilita botões de refresh, assinatura e cancelar
    this._enterInProcess(ViewController.PROC_CANCEL);

    this.service.cancelCurrentProcess();
    this.dialog.dialog("close");
};

/**
 * Marca que este controle entra em um processo específico, habilitando ou travando botões de ação
 * @param {number} procType Tipo do processo
 */
ViewController.prototype._enterInProcess = function(procType)
{
    switch (procType)
    {
        case ViewController.PROC_NONE:
            this._enableButtons();
            break;
        case ViewController.PROC_SIGN:
            this._disableButtonsByClass(this.uiSettings["sign-button-class"], this.uiSettings["refresh-button-class"]);
            break;
        case ViewController.PROC_CANCEL:
            this._disableButtonsByClass(this.uiSettings["cancel-button-class"], this.uiSettings["sign-button-class"], this.uiSettings["refresh-button-class"]);
            break;
        default:
            throw new Error("UI entrando em estado de processo inválido");
    }

    //muda estado do processo atual para o passado
    this._currentProcess = procType;
};

//hack: não mude a assinatura deste método, pois qcert faz monkey-patch aqui
ViewController.prototype._onSignatureProgressChanged = function(progress)
{
    var completionRate = (progress.done / progress.total) * 100;
    var progressText   = progress.done + " - " + progress.total;

    return this._updateProgressBar(completionRate, progressText);
};

//hack: não mude a assinatura deste método, pois qcert faz monkey-patch aqui
ViewController.prototype._updateProgressBar = function(completionRate, progressText)
{
    this.dialog.find(this.uiSettings["sel-progress"]).progressbar({ value: completionRate });
    this.dialog.find(this.uiSettings["sel-progress-text"]).text(progressText);
};

ViewController.prototype._onSignButtonClick = function () {
    //garante que não estamos em nenhum processo atual
    if (this._currentProcess !== ViewController.PROC_NONE) return;

    var selectedCert = this.getSelectedCertificate();
    if (!selectedCert) return;

    // Armazena o certificado utilizado (Nome amigável) para sugerir no momento da assinatura e próximo login.
    this.setDefaultCertificate(selectedCert.certID);

    //desabilita botões de refresh e assinatura
    this._enterInProcess(ViewController.PROC_SIGN);
    this.dialog.find(this.uiSettings["sel-cert-list"]).attr("disabled", true);

    //timer para assinatura
    let timer = null;
    
    var jTimerElement = this.dialog.find(this.uiSettings["sel-timer"]);
    if (jTimerElement.length) {
        timer = new Stopwatch(jTimerElement[0], {
            delay: 1000
        });
        timer.start();    
    }

    let dialogCloseFunc = () => {
        //para o timer
        timer.stop();

        //fecha dialog
        try {
            setTimeout(() => { this.dialog.dialog("close") }, this.uiSettings["dialog-close-delay"]);
        } catch (err) {
            console.log(err);
        }
    };

    this.service.startSignProcess(selectedCert, {
            onProgressChanged: (progress) => this._onSignatureProgressChanged(progress)
        })
        .then(dialogCloseFunc)
        .then(() => {
            this.event.emit("signatureFinished", null);
        })
        .catch(err => { 
            this.event.emit("onDisplayError", err);
            this.event.emit("signatureFinished", err);
            dialogCloseFunc();
        });
};

var Stopwatch = function (elem, options) {

    var timer = createTimer(),
        startTime,
        interval;

    // default options
    options = options || {};
    options.delay = options.delay || 1;

    // append elements     
    elem.appendChild(timer);

    // private functions
    function createTimer() {
        return document.createElement("span");
    }

    function start() {
        if (!interval) {
            startTime = Date.now();
            interval = setInterval(update, options.delay);
        }
    }

    function stop() {
        if (interval) {
            clearInterval(interval);
            elem.innerHTML = "";
        }
    }

    function padLeft(nr, n, str) {
        return Array(n - String(nr).length + 1).join(str || '0') + nr;
    }

    function update() {

        // later record end time
        var endTime = new Date();

        // time difference in ms
        var timeDiff = endTime - startTime;

        // strip the miliseconds
        timeDiff /= 1000;

        // get seconds
        var seconds = Math.round(timeDiff % 60);

        // remove seconds from the date
        timeDiff = Math.floor(timeDiff / 60);

        // get minutes
        var minutes = Math.round(timeDiff % 60);

        // remove minutes from the date
        //timeDiff = Math.floor(timeDiff / 60);

        // get hours
        //var hours = Math.round(timeDiff % 24);

        timer.innerHTML = padLeft(minutes, 2, "0") + "m " + padLeft(seconds, 2, "0") + "s";
    }

    // public API
    this.start = start;
    this.stop = stop;
};

module.exports = ViewController;


// WEBPACK FOOTER //
// ./C:/Projetos_Git/qframework_client/src/gui_js/lib/viewcontroller.js